import { useEffect, useState, useCallback } from "react";
import { InfoHint } from "../components/InfoHint";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Users, Plus, Pencil, Trash2, Phone, Mail, KeyRound, Upload, Download, Building2, Copy, CheckCircle2, AlertTriangle, ShieldCheck, Ban, Users2, Search,
} from "lucide-react";
import { CestinoButton } from "../components/CestinoButton";
import { EmailMultiTenantNotice } from "../components/EmailMultiTenantNotice";
import { MultiTenantBadge } from "../components/MultiTenantBadge";

const EMPTY = {
  nome: "", condominio_id: "", unita: "", scala: "", telefono: "", email: "",
  codice_fiscale: "", quota_millesimale: "", note: "", consigliere: false, crea_account: false,
};

export default function Residenti() {
  const [list, setList] = useState([]);
  const [condomini, setCondomini] = useState([]);
  const [filterCond, setFilterCond] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [genCred, setGenCred] = useState(null);
  // import
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [invioCredenziali, setInvioCredenziali] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [stato, setStato] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [statoFiltro, setStatoFiltro] = useState("tutti");
  // Iter122di · toggle "Solo condivisi con altri Studi" — filtra i condòmini che hanno
  // un account attivo anche presso altri Studi Ginius (altri_tenant_count > 0).
  const [soloCondivisi, setSoloCondivisi] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [fixResult, setFixResult] = useState(null);
  // Iter122k85 · ricerca testuale globale su tutti gli account (nome, email, login_id,
  // codice fiscale, telefono, condominio, unità).
  const [search, setSearch] = useState("");

  const matchSearch = (r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const hay = [r.nome, r.email, r.login_id, r.codice_fiscale, r.telefono,
                  r.condominio_nome, r.condominio, r.unita, r.scala]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  };

  const matchStato = (r) => {
    if (statoFiltro === "attivati") return r.has_account && r.attivo;
    if (statoFiltro === "da_attivare") return r.has_account && !r.attivo;
    return true;
  };
  const [aggregatiIds, setAggregatiIds] = useState(new Set());
  const displayList = list.filter(matchStato).filter(matchSearch).filter((r) => !soloCondivisi || (r.altri_tenant_count || 0) > 0);
  const toggleStato = (v) => setStatoFiltro((s) => (s === v ? "tutti" : v));

  const load = useCallback(() => {
    const params = filterCond ? { condominio_id: filterCond } : {};
    api.get("/residenti", { params }).then((r) => setList(r.data)).catch(() => {});
    api.get("/residenti/stato-attivazioni", { params }).then((r) => setStato(r.data)).catch(() => {});
    // Iter122fw · carica gli aggregati candidati (solo quando filtri su un singolo condominio)
    if (filterCond) {
      api.get(`/condomini/${filterCond}/aggregati-candidati`).then((r) => {
        setAggregatiIds(new Set((r.data || []).map((a) => a.aggregato_id)));
      }).catch(() => setAggregatiIds(new Set()));
    } else {
      setAggregatiIds(new Set());
    }
  }, [filterCond]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/condomini").then((r) => setCondomini(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/settings").then((r) => setInvioCredenziali(!!r.data.invio_credenziali)).catch(() => {}); }, []);

  const toggleInvio = async (val) => {
    setInvioCredenziali(val);
    try {
      await api.put("/settings", { invio_credenziali: val });
      toast.success(val
        ? "Invio credenziali ATTIVATO: le email partiranno alla creazione degli account."
        : "Invio credenziali sospeso: nessuna email verrà inviata ai condòmini.");
    } catch { setInvioCredenziali(!val); toast.error("Impossibile aggiornare l'impostazione"); }
  };

  const sollecita = async () => {
    setBulkSending(true);
    try {
      const params = filterCond ? { condominio_id: filterCond } : {};
      const { data } = await api.post("/residenti/sollecita-attivazioni", null, { params });
      toast.success(`Sollecito inviato a ${data.sollecitati} condòmino/i non ancora attivati.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Invio non riuscito"); }
    finally { setBulkSending(false); }
  };

  const correggiLoginId = async () => {
    if (!filterCond) { toast.error("Seleziona prima un condominio dal menu a tendina."); return; }
    const nomeCond = condomini.find((c) => c.id === filterCond)?.nome || "questo condominio";
    if (!window.confirm(`Rigenerare gli ID di accesso di "${nomeCond}" usando l'ordine Cognome Nome?\n\nGli ID errati (es. r.mario) verranno corretti (es. m.rossi). Gli interessati dovranno usare il NUOVO ID (l'email continua a funzionare sempre).`)) return;
    setFixing(true);
    try {
      const { data } = await api.post("/residenti/correggi-login-id", null, { params: { condominio_id: filterCond } });
      setFixResult(data);
      if (data.corretti === 0) toast.success("Nessun ID da correggere: sono già tutti corretti.");
      else toast.success(`${data.corretti} ID di accesso corretti.`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Correzione non riuscita"); }
    finally { setFixing(false); }
  };

  const openNew = () => { setForm(EMPTY); setEditId(null); setError(""); setGenCred(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...EMPTY, ...r, crea_account: false }); setEditId(r.id); setError(""); setGenCred(null); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setError("");
    try {
      const res = editId
        ? await api.put(`/residenti/${editId}`, form)
        : await api.post("/residenti", form);
      if (res.data.credenziali) {
        setGenCred({ nome: form.nome, email: form.email, emailInviata: res.data.email_inviata, ...res.data.credenziali });
        toast.success(res.data.email_inviata
          ? "Account creato. Email di attivazione inviata al condòmino."
          : "Account creato. Comunica ID e codice di attivazione al condòmino.");
      } else if (res.data.collegato) {
        toast.success("Account collegato: questa persona aveva già un account attivo in un altro condominio. Ora è collegato anche a questa unità.");
        setOpen(false);
      } else {
        setOpen(false);
      }
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const resendCredenziali = async (r) => {
    try {
      const { data } = await api.post(`/residenti/${r.id}/invia-credenziali`);
      toast.success(`Credenziali reinviate a ${data.email}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Invio non riuscito"); }
  };

  const riemettiCredenziali = async (r) => {
    if (!window.confirm(`Riemettere le credenziali di accesso per ${r.nome}?\n\nVerrà generato un NUOVO codice di attivazione, la password attuale sarà azzerata e verrà inviata subito una nuova email di attivazione.`)) return;
    try {
      const { data } = await api.post(`/residenti/${r.id}/riemetti-credenziali`);
      setGenCred({ nome: r.nome, email: data.email, emailInviata: data.email_inviata, login_id: data.login_id, activation_code: data.activation_code });
      setEditId(r.id); setOpen(true);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Operazione non riuscita"); }
  };

  const toggleSel = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const invioSelezione = async () => {
    try {
      const { data } = await api.post("/residenti/invia-credenziali-selezione", { ids: Array.from(selected) });
      toast.success(`Credenziali inviate a ${data.programmati} condòmin${data.programmati === 1 ? "o" : "i"} selezionat${data.programmati === 1 ? "o" : "i"}`);
      setSelected(new Set());
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Invio non riuscito"); }
  };

  const toggleAccount = async (r) => {
    const nuovo = !r.disabilitato;
    if (nuovo && !window.confirm(`Disabilitare l'account di ${r.nome}? Non potrà più accedere finché non lo riabiliti.`)) return;
    try {
      await api.post(`/residenti/${r.id}/disabilita`, { disabilitato: nuovo });
      toast.success(nuovo ? "Account disabilitato" : "Account riabilitato");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Operazione non riuscita"); }
  };

  const downloadConsenso = async (r) => {
    try {
      const res = await api.get(`/residenti/${r.id}/consenso/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `consenso_privacy_${r.login_id || r.nome}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Impossibile scaricare il modulo di consenso"); }
  };

  const remove = async (r) => {
    const alsoAccount = r.has_account ? window.confirm("Eliminare anche l'account di accesso del condòmino?") : false;
    await api.delete(`/residenti/${r.id}?elimina_account=${alsoAccount}`);
    load();
  };

  const downloadTemplate = async () => {
    const res = await api.get("/residenti/template", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "modello_condomini.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCredenziali = async () => {
    try {
      const params = filterCond ? { condominio_id: filterCond } : {};
      const res = await api.get("/residenti/credenziali", { responseType: "blob", params });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "credenziali_condomini.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Impossibile scaricare le credenziali"); }
  };

  const doImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/residenti/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      load();
      toast.success(`${data.created} condòmini importati`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setImporting(false); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copiato"); };

  return (
    <div className="max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-3">
            <Users className="w-9 h-9 text-brand" /> Account Condòmini
          </h1>
          <InfoHint section="residenti" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="download-template-button" variant="outline" className="h-12" onClick={downloadTemplate}>
            <Download className="w-5 h-5 mr-2" /> Scarica modello Excel
          </Button>
          <Button data-testid="download-credenziali-button" variant="outline" className="h-12" onClick={downloadCredenziali}>
            <KeyRound className="w-5 h-5 mr-2" /> Credenziali accesso
          </Button>
          <Button data-testid="open-import-button" variant="outline" className="h-12" onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }}>
            <Upload className="w-5 h-5 mr-2" /> Importa Excel
          </Button>
          <Button data-testid="add-residente-button" onClick={openNew} className="h-12 px-5 text-base font-bold bg-brand hover:bg-brand-dark">
            <Plus className="w-5 h-5 mr-2" /> Aggiungi
          </Button>
          <CestinoButton entity="residenti" onChange={load} size="h-12 text-base" />
        </div>
      </div>

      <Card className={`p-4 border-2 mb-4 ${invioCredenziali ? "border-green-300 bg-green-50/50" : "border-amber-300 bg-amber-50/50"}`}>
        <div className="flex items-start gap-3">
          <Switch data-testid="toggle-invio-credenziali" checked={invioCredenziali} onCheckedChange={toggleInvio} className="mt-1" />
          <div>
            <div className="font-bold text-lg">Invio automatico delle credenziali ai condòmini</div>
            <div className="text-base text-slate-600 max-w-3xl">
              {invioCredenziali
                ? "ATTIVO: alla creazione di un account il condòmino riceve via email ID e codice di attivazione."
                : "SOSPESO: gli account vengono creati ma nessuna email parte. Attivalo solo quando l'app è pubblicata e pronta per i condòmini."}
            </div>
          </div>
        </div>
      </Card>

      {stato && stato.totale_account > 0 && (
        <Card data-testid="stato-attivazioni-card" className="p-5 border-2 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-brand" />
              <h3 className="font-display text-xl font-bold">Stato attivazioni</h3>
            </div>
            <Button data-testid="sollecita-attivazioni-btn" variant="outline" disabled={!invioCredenziali || bulkSending || stato.da_attivare === 0}
              className="h-11 border-amber-400 text-amber-800" onClick={sollecita}
              title={!invioCredenziali ? "Attiva prima l'invio credenziali" : "Invia un sollecito ai non attivati"}>
              <Mail className="w-5 h-5 mr-2" /> {bulkSending ? "Invio..." : "Sollecita non attivati"}
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <button type="button" data-testid="filter-stato-tutti" onClick={() => setStatoFiltro("tutti")}
              className={`text-center p-3 rounded-lg bg-slate-50 border transition-all ${statoFiltro === "tutti" ? "ring-2 ring-brand border-brand" : "hover:border-slate-400"}`}>
              <div data-testid="stat-totale" className="text-3xl font-extrabold">{stato.totale_account}</div>
              <div className="text-sm font-semibold uppercase tracking-wide text-slate-500">Account</div>
            </button>
            <button type="button" data-testid="filter-stato-attivati" onClick={() => toggleStato("attivati")}
              className={`text-center p-3 rounded-lg bg-green-50 border border-green-200 transition-all ${statoFiltro === "attivati" ? "ring-2 ring-green-600" : "hover:border-green-400"}`}>
              <div data-testid="stat-attivati" className="text-3xl font-extrabold text-green-700">{stato.attivati}</div>
              <div className="text-sm font-semibold uppercase tracking-wide text-green-700">Attivati</div>
            </button>
            <button type="button" data-testid="filter-stato-da-attivare" onClick={() => toggleStato("da_attivare")}
              className={`text-center p-3 rounded-lg bg-amber-50 border border-amber-200 transition-all ${statoFiltro === "da_attivare" ? "ring-2 ring-amber-600" : "hover:border-amber-400"}`}>
              <div data-testid="stat-da-attivare" className="text-3xl font-extrabold text-amber-700">{stato.da_attivare}</div>
              <div className="text-sm font-semibold uppercase tracking-wide text-amber-700">Da attivare</div>
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-4 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full bg-green-600 transition-all" style={{ width: `${stato.percentuale}%` }} />
            </div>
            <span className="font-bold text-lg text-green-700">{stato.percentuale}%</span>
          </div>
        </Card>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        {/* Iter122k85 · Ricerca testuale globale (nome, email, login, CF, telefono, condominio, unità) */}
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="residenti-search"
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome, email, ID accesso, CF, telefono, condominio, unità…"
            className="h-12 pl-9 pr-9 border-2 border-slate-300 bg-white text-base" />
          {search && (
            <button type="button" data-testid="residenti-search-clear"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              aria-label="Pulisci ricerca">
              ✕
            </button>
          )}
        </div>
        <select data-testid="filter-condominio" value={filterCond} onChange={(e) => setFilterCond(e.target.value)}
          className="h-12 px-3 border-2 border-slate-300 rounded-lg bg-white text-base">
          <option value="">Tutti i condomìni</option>
          {condomini.map((c) => {
            const n = c.account_count ?? 0;
            const flag = n > 0 ? `✓ ${n} account` : "· nessun account";
            return <option key={c.id} value={c.id}>{c.nome} {flag}</option>;
          })}
        </select>
        <Button data-testid="correggi-login-id-btn" variant="outline" className="h-12 border-brand/40 text-brand"
          disabled={!filterCond || fixing} onClick={correggiLoginId}
          title={!filterCond ? "Seleziona prima un condominio" : "Rigenera gli ID di accesso (Cognome Nome)"}>
          <KeyRound className="w-5 h-5 mr-2" /> {fixing ? "Correzione..." : "Correggi ID accesso (Cognome Nome)"}
        </Button>
        {/* Iter122di · toggle "Solo condivisi con altri Studi" */}
        {(() => {
          const nCondivisi = list.filter((r) => (r.altri_tenant_count || 0) > 0).length;
          if (nCondivisi === 0 && !soloCondivisi) return null;
          return (
            <button
              type="button"
              data-testid="filter-condivisi-toggle"
              onClick={() => setSoloCondivisi((v) => !v)}
              className={`h-12 px-3 rounded-lg border-2 inline-flex items-center gap-2 text-sm font-semibold transition-colors ${
                soloCondivisi ? "bg-sky-600 border-sky-600 text-white" : "bg-white border-sky-300 text-sky-800 hover:bg-sky-50"
              }`}
              title="Mostra solo condòmini con account attivo anche presso altri Studi Ginius"
            >
              <Users2 className="w-4 h-4" /> Solo condivisi ({nCondivisi})
            </button>
          );
        })()}
      </div>

      {list.some((r) => r.has_account) && (
        <div className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded-lg bg-slate-50 border-2">
          <Button data-testid="select-all-accounts" variant="outline" size="sm" className="h-10"
            onClick={() => {
              const withAcc = list.filter((r) => r.has_account).map((r) => r.id);
              const allSel = withAcc.length > 0 && withAcc.every((id) => selected.has(id));
              setSelected(allSel ? new Set() : new Set(withAcc));
            }}>
            {list.filter((r) => r.has_account).every((r) => selected.has(r.id)) && selected.size > 0 ? "Deseleziona tutti" : "Seleziona tutti"}
          </Button>
          <span className="text-sm text-slate-600 font-semibold">{selected.size} selezionat{selected.size === 1 ? "o" : "i"}</span>
          <Button data-testid="invia-selezione-btn" size="sm" disabled={!invioCredenziali || selected.size === 0}
            className="h-10 bg-brand hover:bg-brand-dark" onClick={invioSelezione}
            title={!invioCredenziali ? "Attiva prima l'invio credenziali (riquadro in alto)" : "Invia le credenziali solo ai condòmini selezionati"}>
            <Mail className="w-4 h-4 mr-2" /> Invia credenziali ai selezionati
          </Button>
        </div>
      )}

      {(statoFiltro !== "tutti" || search.trim()) && (
        <div className="flex items-center gap-3 mb-3 text-sm" data-testid="stato-filtro-banner">
          <span className="font-semibold text-slate-600">
            Filtro attivo: {[statoFiltro !== "tutti" ? (statoFiltro === "attivati" ? "Attivati" : "Da attivare") : null,
                             search.trim() ? `“${search.trim()}”` : null].filter(Boolean).join(" · ")}
            {" "}({displayList.length})
          </span>
          <button data-testid="reset-stato-filtro" onClick={() => { setStatoFiltro("tutti"); setSearch(""); }} className="text-brand font-semibold hover:underline">Mostra tutti</button>
        </div>
      )}
      {list.length === 0 ? (
        <Card className="p-5 text-center text-slate-500 text-lg border-2">
          Nessun condòmino registrato. Aggiungilo manualmente o importa un file Excel.
        </Card>
      ) : displayList.length === 0 ? (
        <Card className="p-5 text-center text-slate-500 text-lg border-2">
          Nessun condòmino corrisponde al filtro selezionato.
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {displayList.map((r) => (
            <Card key={r.id} data-testid={`residente-card-${r.nome}`} className={`p-5 border-2 min-w-0 ${r.disabilitato ? "opacity-70 border-red-200 bg-red-50/30" : ""}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-start gap-2 min-w-0">
                  {r.has_account && (
                    <input type="checkbox" data-testid={`select-${r.nome}`} className="mt-1.5 w-4 h-4 accent-brand cursor-pointer"
                      checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} />
                  )}
                  <div className="min-w-0">
                    <h3 className="font-bold text-lg truncate flex items-center gap-2 flex-wrap">
                      {r.nome}
                      {r.consigliere && (
                        <span data-testid={`consigliere-badge-${r.nome}`} className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#F0D9E1] text-brand border border-[#C98AA0] inline-flex items-center gap-1">
                          <ShieldCheck className="w-3 h-3" /> Consigliere
                        </span>
                      )}
                      <MultiTenantBadge count={r.altri_tenant_count} testid={`multitenant-badge-${r.nome}`} />
                      {aggregatiIds.has(r.soggetto_id || r.id) && (
                        <span data-testid={`aggregato-badge-${r.nome}`}
                          title="Soggetto aggregato: sono presenti soggetti individuali sulla stessa unità. Puoi sostituirlo dall'Import PDF Danea."
                          className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-900 border border-indigo-300 inline-flex items-center gap-1">
                          🔀 Aggregato
                        </span>
                      )}
                    </h3>
                    <div className="text-base text-slate-600 flex items-center gap-1 min-w-0"><Building2 className="w-4 h-4 shrink-0" /> <span className="truncate">{r.condominio_nome}</span></div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {r.has_account && (
                    <div className="flex items-center gap-2 mb-1" title={r.disabilitato ? "Account sospeso: riabilita per riconsentire l'accesso" : "Account attivo: disattiva per sospendere temporaneamente l'accesso senza perdere le credenziali"}>
                      <span className={`text-xs font-bold ${r.disabilitato ? "text-red-700" : "text-emerald-700"}`}>
                        {r.disabilitato ? "Sospeso" : "Attivo"}
                      </span>
                      <Switch
                        data-testid={`toggle-account-switch-${r.nome}`}
                        checked={!r.disabilitato}
                        onCheckedChange={() => toggleAccount(r)}
                        className={r.disabilitato ? "data-[state=unchecked]:bg-red-400" : "data-[state=checked]:bg-emerald-600"}
                      />
                    </div>
                  )}
                  {r.has_account && (
                    <span className={`px-2 py-1 rounded text-xs font-bold flex items-center gap-1 ${r.attivo ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`} title={r.attivo ? "Account attivato" : "In attesa di attivazione"}>
                      <KeyRound className="w-3.5 h-3.5" /> {r.attivo ? "Attivato" : "Da attivare"}
                    </span>
                  )}
                  {r.disabilitato && (
                    <span data-testid={`disabled-badge-${r.nome}`} className="px-2 py-1 rounded text-xs font-bold flex items-center gap-1 bg-red-100 text-red-800">
                      <Ban className="w-3.5 h-3.5" /> Sospeso
                    </span>
                  )}
                </div>
              </div>
              <div className="text-base text-slate-600 space-y-1 mb-3">
                {(r.unita || r.scala) && <div>Unità: <b>{[r.scala, r.unita].filter(Boolean).join(" · ") || "—"}</b></div>}
                {r.telefono && <div className="flex items-center gap-2 min-w-0"><Phone className="w-4 h-4 shrink-0" /> <span className="truncate">{r.telefono}</span></div>}
                {r.email && <div className="flex items-center gap-2 min-w-0"><Mail className="w-4 h-4 shrink-0" /> <span className="truncate">{r.email}</span></div>}
                {r.quota_millesimale && <div>Millesimi: <b>{r.quota_millesimale}</b></div>}
                {r.consenso_at && (
                  <div className="flex items-center gap-2 text-green-700 font-semibold"><ShieldCheck className="w-4 h-4" /> Consenso privacy acquisito</div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button data-testid={`edit-residente-${r.nome}`} variant="outline" size="sm" className="h-10" onClick={() => openEdit(r)}>
                  <Pencil className="w-4 h-4 mr-1" /> Modifica
                </Button>
                {r.has_account && (
                  <Button data-testid={`resend-credenziali-${r.nome}`} variant="outline" size="sm" className="h-10" title="Reinvia credenziali (solo se non ancora attivato)" onClick={() => resendCredenziali(r)}>
                    <Mail className="w-4 h-4" />
                  </Button>
                )}
                {r.has_account && (
                  <Button data-testid={`riemetti-credenziali-${r.nome}`} variant="outline" size="sm" className="h-10 text-brand border-brand/30 hover:bg-[#FBEEF3]" title="Riemetti credenziali: rigenera il codice e reinvia l'email (anche se già attivato)" onClick={() => riemettiCredenziali(r)}>
                    <KeyRound className="w-4 h-4 mr-1" /> Riemetti
                  </Button>
                )}
                {r.consenso_at && (
                  <Button data-testid={`consenso-pdf-${r.nome}`} variant="outline" size="sm" className="h-10 text-brand border-[#EBC5D2]" title="Scarica il modulo di consenso privacy" onClick={() => downloadConsenso(r)}>
                    <ShieldCheck className="w-4 h-4 mr-1" /> Consenso
                  </Button>
                )}
                <Button data-testid={`delete-residente-${r.nome}`} variant="outline" size="sm" className="h-10 text-red-700 border-red-300" onClick={() => remove(r)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-2xl">{editId ? "Modifica condòmino" : "Nuovo condòmino"}</DialogTitle></DialogHeader>
          {genCred ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-800 font-bold mb-2"><CheckCircle2 className="w-5 h-5" /> Account creato</div>
                {genCred.emailInviata && (
                  <div className="flex items-center gap-2 text-base text-green-800 font-semibold mb-2 bg-green-100 rounded-lg px-3 py-2">
                    <Mail className="w-4 h-4" /> Email di attivazione inviata a {genCred.email}
                  </div>
                )}
                <p className="text-base text-slate-700 mb-3">Comunica al condòmino queste credenziali (sono anche nell'email). Alla prima attivazione sceglierà la propria password.</p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between bg-white border rounded-lg px-3 py-2">
                    <span className="text-base font-mono"><b>ID di accesso:</b> {genCred.login_id}</span>
                    <Button size="sm" variant="ghost" onClick={() => copy(genCred.login_id)}><Copy className="w-4 h-4" /></Button>
                  </div>
                  <div className="flex items-center justify-between bg-white border rounded-lg px-3 py-2">
                    <span className="text-base font-mono"><b>Codice attivazione:</b> {genCred.activation_code}</span>
                    <Button size="sm" variant="ghost" data-testid="copy-activation" onClick={() => copy(genCred.activation_code)}><Copy className="w-4 h-4" /></Button>
                  </div>
                  {genCred.email && (
                    <div className="flex items-center justify-between bg-white border rounded-lg px-3 py-2">
                      <span className="text-base"><b>Email:</b> {genCred.email}</span>
                      <Button size="sm" variant="ghost" onClick={() => copy(genCred.email)}><Copy className="w-4 h-4" /></Button>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="close-genpassword" onClick={() => { setOpen(false); setGenCred(null); }} className="h-12 bg-brand hover:bg-brand-dark">Ho annotato, chiudi</Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                <F label="Nome e cognome" required v={form.nome} on={(v) => set("nome", v)} testid="res-nome" />
                <div>
                  <Label className="text-sm font-bold">Condominio<span className="text-red-500 ml-0.5">*</span></Label>
                  <select data-testid="res-condominio" value={form.condominio_id} onChange={(e) => set("condominio_id", e.target.value)}
                    className="h-12 mt-1 w-full border-2 border-slate-300 rounded-lg px-3 bg-white">
                    <option value="">Seleziona...</option>
                    {condomini.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Scala" v={form.scala} on={(v) => set("scala", v)} />
                  <F label="Interno / Unità" v={form.unita} on={(v) => set("unita", v)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Telefono" v={form.telefono} on={(v) => set("telefono", v)} />
                  <F label="Email" v={form.email} on={(v) => set("email", v)} testid="res-email" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Codice fiscale" v={form.codice_fiscale} on={(v) => set("codice_fiscale", v)} />
                  <F label="Quota millesimale" v={form.quota_millesimale} on={(v) => set("quota_millesimale", v)} />
                </div>
                <label className="flex items-center gap-3 p-3 border-2 border-slate-200 rounded-lg cursor-pointer hover:border-brand/40" data-testid="res-consigliere-toggle">
                  <input type="checkbox" className="w-5 h-5 accent-brand" checked={!!form.consigliere}
                    onChange={(e) => set("consigliere", e.target.checked)} data-testid="res-consigliere" />
                  <div>
                    <div className="font-semibold text-base flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-brand" /> Consigliere di condominio</div>
                    <div className="text-sm text-slate-500">Se attivo, potrà essere selezionato in blocco per gli avvisi al consiglio.</div>
                  </div>
                </label>
                {!(editId && form.has_account) && (
                  <div className="flex items-center justify-between p-3 border-2 border-slate-200 rounded-lg">
                    <div>
                      <div className="font-semibold text-base flex items-center gap-2"><KeyRound className="w-5 h-5" /> Crea account di accesso</div>
                      <div className="text-sm text-slate-500">Genera email+password per inviare manutenzioni</div>
                    </div>
                    <Switch data-testid="res-crea-account" checked={form.crea_account} onCheckedChange={(v) => set("crea_account", v)} />
                  </div>
                )}
                <EmailMultiTenantNotice email={form.email} enabled={!!form.crea_account && !form.has_account} />
                {editId && form.has_account && (
                  <div className="p-3 bg-green-50 border-2 border-green-100 rounded-lg text-base text-green-800 flex items-center gap-2">
                    <KeyRound className="w-4 h-4" /> Questo condòmino ha già un account attivo.
                  </div>
                )}
                {error && <div className="p-3 bg-red-50 text-red-800 rounded-lg text-base font-semibold">{error}</div>}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="h-12">Annulla</Button>
                <Button data-testid="save-residente-button" onClick={save} disabled={!form.nome || !form.condominio_id} className="h-12 bg-brand hover:bg-brand-dark">Salva</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-2xl flex items-center gap-2"><Upload className="w-6 h-6" /> Importa condòmini da Excel</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-[#FBEEF3] border-2 border-[#F0D9E1] rounded-lg text-base text-slate-700">
              Usa il <b>Modello Excel</b> con le colonne: nome, condominio, unita, scala, telefono, email, codice_fiscale, quota_millesimale, crea_account (si/no).
              Il valore della colonna <b>condominio</b> deve corrispondere al nome di un condominio esistente.
              <Button variant="link" className="px-1 h-auto text-brand" onClick={downloadTemplate}>Scarica il modello</Button>
            </div>
            <Input data-testid="import-file-input" type="file" accept=".xlsx" className="h-12"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)} />

            {importResult && (
              <div className="space-y-3">
                <div className="p-3 bg-green-50 border-2 border-green-200 rounded-lg text-green-800 font-semibold flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" /> {importResult.created} condòmini importati
                </div>
                {importResult.accounts?.length > 0 && (
                  <div className="border-2 rounded-lg p-3">
                    <div className="font-bold text-base mb-2 flex items-center gap-2"><KeyRound className="w-4 h-4" /> Account creati ({importResult.accounts.length})</div>
                    <div className="max-h-48 overflow-y-auto space-y-1 text-sm font-mono">
                      {importResult.accounts.map((a, i) => (
                        <div key={i} className="flex items-center justify-between bg-slate-50 rounded px-2 py-1">
                          <span>{a.nome} · ID: {a.login_id} · Cod: {a.activation_code}</span>
                          <Button size="sm" variant="ghost" onClick={() => copy(`${a.nome} - ID: ${a.login_id} - Codice attivazione: ${a.activation_code}`)}><Copy className="w-4 h-4" /></Button>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">Annota ID e codici di attivazione, oppure scaricali con "Credenziali accesso".</p>
                  </div>
                )}
                {importResult.errors?.length > 0 && (
                  <div className="border-2 border-amber-200 rounded-lg p-3 bg-amber-50">
                    <div className="font-bold text-base mb-1 flex items-center gap-2 text-amber-800"><AlertTriangle className="w-4 h-4" /> Avvisi ({importResult.errors.length})</div>
                    <div className="text-sm text-amber-900 space-y-0.5 max-h-32 overflow-y-auto">
                      {importResult.errors.map((e, i) => <div key={i}>• {e}</div>)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} className="h-12">Chiudi</Button>
            <Button data-testid="do-import-button" onClick={doImport} disabled={!importFile || importing} className="h-12 bg-brand hover:bg-brand-dark">
              {importing ? "Importazione..." : "Importa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!fixResult} onOpenChange={(o) => { if (!o) setFixResult(null); }}>
        <DialogContent className="max-w-lg" data-testid="fix-login-id-dialog">
          <DialogHeader>
            <DialogTitle>Correzione ID di accesso</DialogTitle>
          </DialogHeader>
          <div className="text-sm">
            {fixResult?.corretti === 0 ? (
              <p className="text-slate-600">Nessun ID da correggere: gli ID di accesso erano già nel formato corretto.</p>
            ) : (
              <>
                <p className="mb-3 text-slate-600"><b>{fixResult?.corretti}</b> ID di accesso corretti. Comunica agli interessati il nuovo ID (in alternativa possono sempre accedere con la propria email).</p>
                <div className="max-h-72 overflow-y-auto border rounded-lg divide-y">
                  {(fixResult?.report || []).map((x, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`fix-row-${i}`}>
                      <span className="font-semibold truncate">{x.nome}</span>
                      <span className="text-slate-500 whitespace-nowrap"><span className="line-through">{x.vecchio}</span> → <b className="text-brand">{x.nuovo}</b></span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setFixResult(null)} className="h-12 bg-brand hover:bg-brand-dark" data-testid="fix-login-id-close">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, v, on, testid, required }) {
  return (
    <div>
      <Label className="text-sm font-semibold">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>
      <Input data-testid={testid} value={v} onChange={(e) => on(e.target.value)} className="h-12 mt-1" />
    </div>
  );
}
